import { ComponentPropsWithoutRef, ComponentType, ReactNode } from 'react';

import { cn } from '@/services/utils';

type BadgeVariant = 'info' | 'success' | 'error' | 'warning' | 'neutral' | 'white';
type BadgeType = 'status' | 'count' | 'category';
type BadgeSize = 'default' | 'xl';

interface BadgeProps {
    variant?: BadgeVariant;
    type?: BadgeType;
    /**
     * `default` steps up at `lg`; `xl` keeps the same size on every
     * breakpoint. Ignored for the `count` type.
     */
    size?: BadgeSize;
    number?: ReactNode;
    /** Leading icon. Figma defines it for `size="xl"` only. */
    icon?: ComponentType<ComponentPropsWithoutRef<'svg'> & { title?: string }>;
    /** Required when the badge is icon-only (no `children`). */
    'aria-label'?: string;
    className?: string;
    children?: ReactNode;
}

/**
 * Background token per variant — applied on the wrapper for the `status`
 * and `category` types. The `count` type uses a different solid-color
 * treatment further down.
 */
const VARIANT_BG: Record<BadgeVariant, string> = {
    info: '!tw-bg-info-lighter',
    success: '!tw-bg-success-lighter',
    error: '!tw-bg-error-lighter',
    warning: '!tw-bg-warning-lighter',
    neutral: '!tw-bg-fill-secondary',
    white: '!tw-bg-fill-primary',
};

/** Foreground token for the title span (also used when no `number` is set). */
const VARIANT_TEXT: Record<BadgeVariant, string> = {
    info: '!tw-text-info-darker',
    success: '!tw-text-success-darker',
    error: '!tw-text-error-darker',
    warning: '!tw-text-warning-darker',
    neutral: '!tw-text-t-primary',
    white: '!tw-text-t-primary',
};

/** Icon token per variant: coloured variants reuse the text token, neutral ones use `icon-primary`. */
const VARIANT_ICON: Record<BadgeVariant, string> = {
    info: '!tw-text-info-darker',
    success: '!tw-text-success-darker',
    error: '!tw-text-error-darker',
    warning: '!tw-text-warning-darker',
    neutral: 'tw-icon-primary',
    white: 'tw-icon-primary',
};

function Badge({
    variant = 'info',
    type = 'status',
    size = 'default',
    number,
    icon: IconComponent,
    'aria-label': ariaLabel,
    className,
    children,
}: BadgeProps) {
    const isCount = type === 'count';
    const hasTitle = children !== undefined && children !== null && children !== '';
    const isIconOnly = Boolean(IconComponent) && !hasTitle;

    const wrapperClasses = cn(
        'tw-inline-flex',
        '!tw-items-center',
        '!tw-justify-center',
        'tw-gap-1',
        'tw-m-0',
        'tw-rounded-full',
        'tw-align-top',
        isCount
            ? [
                'tw-min-w-[16px]',
                'tw-px-1',
                'tw-h-4',
                '!tw-text-t-xs',
                'tw-shrink-0',
                'tw-align-top'
            ]
            : size === 'xl'
                ? [
                    'tw-w-max',
                    'tw-px-2',
                    'tw-h-6',
                    'tw-text-t-sm',
                    'tw-shrink-0',
                ]
                : [
                    'tw-w-max',
                    'tw-px-1.5',
                    'tw-h-4',
                    'lg:tw-px-2',
                    'lg:tw-h-5',
                    'tw-text-t-xs',
                    'lg:tw-text-t-sm',
                ],
        // `count` keeps the existing solid-color treatment (white text on a
        // solid error/warning background) — used for notification counts.
        isCount && variant === 'error' && 'tw-bg-error tw-text-white',
        isCount && variant === 'warning' && 'tw-bg-warning tw-text-white',
        // `status` uses the per-span colour tokens defined above. The
        // background goes on the wrapper, the foreground on each span.
        !isCount && VARIANT_BG[variant],
        className
    );

    if (isCount) {
        return <div className={wrapperClasses}>{children}</div>;
    }

    if (IconComponent && size !== 'xl') {
        throw new Error('Badge: `icon` is only defined for `size="xl"`.');
    }

    if (isIconOnly && !ariaLabel) {
        throw new Error('Badge: icon-only badges require an `aria-label`.');
    }

    return (
        <div
            className={wrapperClasses}
            role={isIconOnly ? 'img' : undefined}
            aria-label={isIconOnly ? ariaLabel : undefined}
        >
            {IconComponent && (
                <IconComponent
                    className={cn('tw-w-4 tw-h-4 tw-shrink-0', VARIANT_ICON[variant])}
                    aria-hidden
                />
            )}
            {number !== undefined && number !== null && (
                <span className={cn(VARIANT_TEXT[variant], 'tw-text-trim')}>{number}</span>
            )}
            {hasTitle && <span className={cn(VARIANT_TEXT[variant], 'tw-text-trim')}>{children}</span>}
        </div>
    );
}

export { Badge };
export type { BadgeProps, BadgeSize, BadgeType, BadgeVariant };
